import { useEffect, useState } from 'react';
import { useGamesStore } from '../../stores/gamesStore';
import { numberInputClass, fieldLabelClass, PlusIcon, TrashIcon } from './shared';

// ============================================
// Вкладка «Награды»: редактор game_reward_tiers и лимитов игр
// ============================================

const emptyTier = () => ({ id: 0, min_score: '', stars: '' });

const toTierValue = (value) => (value === '' || value === null ? '' : String(value));

const GameCard = ({ game, onSave, isSaving }) => {
  // Черновик: правки не уходят в базу, пока не нажата «Сохранить».
  const [draft, setDraft] = useState(() => ({
    ...game,
    tiers: game.tiers.map((tier) => ({ ...tier })),
  }));
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    setDraft({ ...game, tiers: game.tiers.map((tier) => ({ ...tier })) });
    setLocalError(null);
  }, [game]);

  const updateField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setLocalError(null);
  };

  const updateTier = (index, field, value) => {
    setDraft((current) => ({
      ...current,
      tiers: current.tiers.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier)),
    }));
    setLocalError(null);
  };

  const addTier = () => {
    setDraft((current) => ({
      ...current,
      tiers: [...current.tiers, emptyTier()].sort((a, b) => {
        const aScore = Number(a.min_score) || 0;
        const bScore = Number(b.min_score) || 0;
        return aScore - bScore;
      }),
    }));
  };

  const removeTier = (index) => {
    setDraft((current) => ({
      ...current,
      tiers: current.tiers.filter((_, i) => i !== index),
    }));
    setLocalError(null);
  };

  const handleSave = async () => {
    const tiers = [];
    const seen = new Set();

    for (const tier of draft.tiers) {
      const minScoreRaw = toTierValue(tier.min_score);
      const starsRaw = toTierValue(tier.stars);

      if (minScoreRaw === '' || starsRaw === '') {
        setLocalError('Заполните счёт и награду в каждом пороге');
        return;
      }

      const minScore = Number(minScoreRaw);
      const stars = Number(starsRaw);

      if (!Number.isInteger(minScore) || minScore < 0) {
        setLocalError('Счёт должен быть целым неотрицательным числом');
        return;
      }

      if (!Number.isInteger(stars) || stars < 0) {
        setLocalError('Награда должна быть целым неотрицательным числом');
        return;
      }

      if (seen.has(minScore)) {
        setLocalError(`Два порога с одинаковым счётом (${minScore}) — оставьте один`);
        return;
      }

      seen.add(minScore);
      tiers.push({ id: tier.id || 0, min_score: minScore, stars });
    }

    if (tiers.length === 0) {
      setLocalError('Нужен хотя бы один порог, иначе игра не будет платить звёзды');
      return;
    }

    setLocalError(null);
    await onSave(game.game_id, {
      is_active: draft.is_active,
      max_stars_per_run: Number(draft.max_stars_per_run) || 0,
      max_stars_per_day: Number(draft.max_stars_per_day) || 0,
      session_ttl_minutes: Number(draft.session_ttl_minutes) || 0,
      max_score_per_minute: Number(draft.max_score_per_minute) || 0,
      tiers,
    });
  };

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-color overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-border-color">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-text-primary">{game.title}</h2>
          <span className="text-xs font-mono text-text-muted">{game.game_id}</span>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={draft.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
              className="h-4 w-4 accent-accent-primary"
            />
            Игра доступна
          </label>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 rounded-lg bg-accent-primary text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {isSaving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>

      {localError && (
        <div className="mx-6 mt-4 bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl px-4 py-3 text-sm">
          {localError}
        </div>
      )}

      {/* Потолки и лимиты */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 px-6 py-4 border-b border-border-color">
        <div>
          <label className={fieldLabelClass} title="0 = брать награду из таблицы порогов">
            Потолок за партию
          </label>
          <input
            type="number"
            min="0"
            value={draft.max_stars_per_run}
            onChange={(e) => updateField('max_stars_per_run', e.target.value)}
            className={numberInputClass}
          />
        </div>
        <div>
          <label className={fieldLabelClass} title="0 = без суточного ограничения">
            Потолок за сутки
          </label>
          <input
            type="number"
            min="0"
            value={draft.max_stars_per_day}
            onChange={(e) => updateField('max_stars_per_day', e.target.value)}
            className={numberInputClass}
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Время сессии, мин</label>
          <input
            type="number"
            min="1"
            value={draft.session_ttl_minutes}
            onChange={(e) => updateField('session_ttl_minutes', e.target.value)}
            className={numberInputClass}
          />
        </div>
        <div>
          <label className={fieldLabelClass} title="0 = отключить проверку правдоподобия">
            Счёт в минуту
          </label>
          <input
            type="number"
            min="0"
            value={draft.max_score_per_minute}
            onChange={(e) => updateField('max_score_per_minute', e.target.value)}
            className={numberInputClass}
          />
        </div>
      </div>

      {/* Пороги наград */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-text-secondary">
            Пороги наград: игрок получает звёзды самого высокого порога, который не превышает его счёт.
          </div>
          <button
            onClick={addTier}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-bg-tertiary text-text-secondary hover:text-text-primary rounded-lg transition-colors"
          >
            <PlusIcon />
            Добавить порог
          </button>
        </div>

        <div className="grid grid-cols-[1fr_1fr_44px] gap-3 px-3 py-2 border-b border-border-color text-xs font-medium text-text-secondary uppercase tracking-wider">
          <div>Счёт от</div>
          <div>Звёзд</div>
          <div />
        </div>

        <div className="divide-y divide-border-color">
          {draft.tiers.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-text-secondary">
              Порогов нет — игра не будет начислять звёзды
            </div>
          ) : (
            draft.tiers.map((tier, index) => (
              <div key={tier.id || `new-${index}`} className="grid grid-cols-[1fr_1fr_44px] gap-3 px-3 py-2 items-center">
                <input
                  type="number"
                  min="0"
                  value={toTierValue(tier.min_score)}
                  onChange={(e) => updateTier(index, 'min_score', e.target.value)}
                  className={numberInputClass}
                  placeholder="0"
                />
                <input
                  type="number"
                  min="0"
                  value={toTierValue(tier.stars)}
                  onChange={(e) => updateTier(index, 'stars', e.target.value)}
                  className={numberInputClass}
                  placeholder="0"
                />
                <button
                  onClick={() => removeTier(index)}
                  className="p-2 text-text-secondary hover:text-error transition-colors"
                  title="Удалить порог"
                >
                  <TrashIcon />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const RewardsTab = () => {
  const { games, isLoadingGames, saveGame } = useGamesStore();
  const [savingGameId, setSavingGameId] = useState(null);

  const handleSave = async (gameId, payload) => {
    setSavingGameId(gameId);
    await saveGame(gameId, payload);
    setSavingGameId(null);
  };

  if (isLoadingGames) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-accent-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {games.map((game) => (
        <GameCard key={game.game_id} game={game} onSave={handleSave} isSaving={savingGameId === game.game_id} />
      ))}
    </div>
  );
};

export { RewardsTab };
